import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useVisits } from '../contexts/VisitsContext';
import './VisitsPage.css';

const STATUS_LABELS = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
};

const VISIT_TYPE_LABELS = {
  site_visit: 'Site visit',
  unit_viewing: 'Unit viewing',
  virtual: 'Virtual',
};

function formatDateTime(iso) {
  const d = new Date(iso);
  const date = d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `${date} · ${time}`;
}

export default function VisitsPage() {
  const { visits, loading, cancelVisit } = useVisits();
  const [cancellingId, setCancellingId] = useState(null);
  const navigate = useNavigate();

  const handleCancel = async (visitId) => {
    setCancellingId(visitId);
    try {
      await cancelVisit(visitId);
    } catch (err) {
      console.error('Failed to cancel visit', err);
    } finally {
      setCancellingId(null);
    }
  };

  if (loading) {
    return (
      <div className="visits-page page-content">
        <h1>Visits</h1>
        <p className="page-subtitle">Loading your visits…</p>
      </div>
    );
  }

  return (
    <div className="visits-page page-content">
      <h1>Visits</h1>
      <p className="page-subtitle">Your scheduled property visits</p>

      {visits.length === 0 ? (
        <div className="visits-empty">
          <div className="visits-empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
          </div>
          <p>No visits scheduled yet.</p>
          <p className="visits-empty-hint">Find a property in the chat and tap "Schedule visit" to book a viewing.</p>
          <button type="button" className="visits-empty-cta" onClick={() => navigate('/')}>
            Find properties
          </button>
        </div>
      ) : (
        <ul className="visits-list">
          {visits.map((v) => (
            <li key={v.id} className="visit-card">
              <div className="visit-main">
                <h3>{v.compound_name}</h3>
                {v.location && (
                  <p className="visit-location">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                      <circle cx="12" cy="10" r="3"/>
                    </svg>
                    {' '}{v.location}
                  </p>
                )}
                <p className="visit-type">{VISIT_TYPE_LABELS[v.visit_type] || v.visit_type}</p>
                <p className="visit-datetime">{formatDateTime(v.scheduled_at)}</p>
                <span className={`visit-status ${v.status}`}>{STATUS_LABELS[v.status] || v.status}</span>
                <div className="visit-actions">
                  <Link to={`/compound/${v.compound_id}`} state={{ freshContext: true }} className="visit-cta">
                    View compound
                  </Link>
                  {(v.status === 'pending' || v.status === 'confirmed') && (
                    <button
                      type="button"
                      className="visit-cancel-btn"
                      onClick={() => handleCancel(v.id)}
                      disabled={cancellingId === v.id}
                    >
                      {cancellingId === v.id ? 'Cancelling…' : 'Cancel'}
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
